import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import type { WebdirectAutoScalingConfiguration } from "@/queries/webdirect-auto-scaling.ts";
import { acknowledgmentSchema, apiUrl } from "@/utils/api.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { handleJsonApiError } from "jsonapi-zod-query";

export type UpdateConfigurationValues = Omit<WebdirectAutoScalingConfiguration, "id"> & {
    hostId: string;
};

export const useUpdateConfigurationMutation = (
    clientId: string,
): UseMutationResult<string, Error, UpdateConfigurationValues> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values) => {
            const { hostId, ...rest } = values;
            const response = await fetch(
                apiUrl(`/clients/${clientId}/hosts/${hostId}/webdirect-auto-scaling/configuration`),
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/vnd.api+json",
                    },
                    body: JSON.stringify({
                        data: {
                            id: hostId,
                            type: "webdirect_auto_scaling_configuration",
                            attributes: rest,
                        },
                    }),
                },
            );
            await handleJsonApiError(response);
            return acknowledgmentSchema.parse(await response.json()).meta.message;
        },
        onSuccess: async (_data, values) => {
            await queryClient.invalidateQueries({
                queryKey: ["client", clientId, "host", values.hostId, "webdirect-auto-scaling"],
            });
        },
    });
};
